import axios, { AxiosInstance } from "axios";

import { getAppConfig } from "@/config";
import { IBusiness } from "@/types/business.type";
import { Invoice } from "@/types/invoice.type";
import { TelecomOperator } from "@/types/payment.type";
import { PublicStorefrontApiResponse } from "@/types/storefront.type";
import { ICollectPaymentResponse, IInitiateCollectionResponse } from "@/types/transaction.type";

/**
 * Resolve the API environment for the hosted checkout. Public payment links open at
 * /payment-links/{shortCode}; a SANDBOX link carries `?type=sandbox`, so the checkout
 * must talk to the sandbox backend. Defaults to LIVE (incl. SSR, where there is no URL).
 */
export type ApiEnvironment = "LIVE" | "SANDBOX";

/** Read the sandbox marker out of a query string. Shared so the server loader
 *  and the browser agree on what makes a request a sandbox request. */
export function environmentFromSearch(search: string): ApiEnvironment {
    return new URLSearchParams(search).get("type") === "sandbox" ? "SANDBOX" : "LIVE";
}

function resolveEnvironment(forced?: ApiEnvironment): ApiEnvironment {
    // A forced value comes from a route loader, which has the request URL and so
    // can tell sandbox from live during SSR. Without it there is no URL to read
    // on the server, hence the LIVE default.
    if (forced) {
        return forced;
    }
    if (typeof window === "undefined") {
        return "LIVE";
    }
    return environmentFromSearch(window.location.search);
}

/**
 * HttpApiFactory is a class that contains all the API endpoints for the application
 */
export class HttpApiFactory {
    private readonly apiGatewayClient: AxiosInstance;
    private _accessToken: string | undefined;
    private _sharedHeaders: { [x: string]: string };

    private readonly _forcedEnvironment?: ApiEnvironment;

    constructor(
        accessToken?: string,
        sharedHeaders: { [x: string]: string } = {},
        forcedEnvironment?: ApiEnvironment
    ) {
        if (accessToken) {
            this._accessToken = accessToken;
        }
        this._forcedEnvironment = forcedEnvironment;

        this.apiGatewayClient = axios.create({
            // SSR (no `window`) runs inside the container where `localhost` isn't the
            // host gateway — use the internal URL there; the browser uses the public one.
            baseURL:
                typeof window === "undefined"
                    ? getAppConfig<string>("apiGatewayUrlInternal")
                    : getAppConfig<string>("apiGatewayUrl"),
            headers: {
                "Content-Type": "application/json",
                "x-environment": "LIVE",
            },
        });
        // Set shared headers
        this._sharedHeaders = sharedHeaders;

        // A request interceptor to set the Authorization header
        this.apiGatewayClient.interceptors.request.use(async (config) => {
            const token = "";

            if (token) {
                this._accessToken = token;
                config.headers["Authorization"] = `Bearer ${token}`;
            } else {
                config.headers["Authorization"] = `Bearer ${this._accessToken}`;
            }

            Object.assign(config.headers, this._sharedHeaders);
            // Route to the sandbox backend when opened from a ?type=sandbox link.
            config.headers["x-environment"] = resolveEnvironment(this._forcedEnvironment);

            return config;
        });
    }

    /**
     * Setter for access token
     */
    set accessToken(token: string) {
        this._accessToken = token;
    }
    /**
     * setter for headers
     */
    set sharedHeaders(headers: { [x: string]: string }) {
        this._sharedHeaders = headers;
    }

    appendSharedHeader(key: string, value: string) {
        this._sharedHeaders[key] = value;
    }

    /**
     * ============================== User API Gateway ==============================
     * - User API Gateway functions
     */

    /**
     *
     * @param data { email: string, returnUrl: string }
     * @returns Returns a stripe identity verification session
     */

    async collectMerchantMomoPaymentApi(payload: {
        uid: string;
        phoneNumber: string;
        amount: number;
        operator: TelecomOperator;
        metadata?: any;
    }): Promise<IInitiateCollectionResponse> {
        const { data } = await this.apiGatewayClient.post(
            `/api/v1/business/${payload.uid}/collect-payment`,
            {
                amount: payload.amount,
                phoneNumber: payload.phoneNumber,
                operator: payload.operator,
                ...(payload.metadata && { metadata: payload.metadata }),
            }
        );

        return data;
    }

    async calculatePaymentFeeApi(payload: {
        amount: number;
        id?: string;
    }): Promise<{ fee: number }> {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/payment/calculate-fee?amount=${payload.amount}&businessId=${payload.id}`
        );

        return data;
    }

    async getPublicBusinessInfoByUid(uid: string): Promise<
        IBusiness & {
            businessOwner?: { name: string; phoneNumber?: string; email?: string };
            settings: { allowReceiptsDownload: boolean };
        }
    > {
        // Public, PII-stripped endpoint (the full /uid endpoint is auth-gated).
        const { data } = await this.apiGatewayClient.get(`/api/v1/business/public-data/${uid}`);

        return data.business;
    }

    async downloadTransactionReceiptApi(payload: { transactionId: string }): Promise<Blob> {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/business/transactions/download/${payload.transactionId}`,
            {
                responseType: "blob",
                headers: {
                    Accept: "application/pdf",
                },
            }
        );

        return data;
    }

    /**
     * ============================== INVOICES API ==============================
     */

    async getOneInvoice(invoiceId: string): Promise<Invoice> {
        const { data } = await this.apiGatewayClient.get(`/api/v1/invoices/${invoiceId}`);

        return data;
    }

    async payInvoiceApi(
        invoiceId: string,
        payload: {
            amount: number;
            paymentType: "COMPLETE" | "PARTIAL";
            paymentMethod: "MOBILE_MONEY" | "CARD";
            phoneNumber: string;
            operator: TelecomOperator;
        }
    ): Promise<ICollectPaymentResponse> {
        const { data } = await this.apiGatewayClient.post(
            `/api/v1/invoices/${invoiceId}/pay`,
            payload
        );

        return data;
    }

    /**
     * Update transaction metadata (for event ticket registrations)
     */
    async updateTransactionMetadataApi(payload: {
        transactionId: string;
        metadata: {
            attendeeName: string;
            attendeePhone: string;
            attendeeWhatsApp: string;
            attendeeEmail: string;
            attendeeTown: string;
            ticketType: any;
            teamName?: string;
            earlyBirdApplied: boolean;
            originalPrice: number;
            finalPrice: number;
        };
    }): Promise<void> {
        await this.apiGatewayClient.patch(
            `/api/v1/business/transactions/${payload.transactionId}/metadata`,
            payload.metadata
        );
    }

    /**
     * Get payment link by short code (public checkout)
     */
    async getPaymentLinkByShortCodeApi(shortCode: string) {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/${encodeURIComponent(shortCode)}`
        );
        return data?.paymentLink ?? data;
    }

    /**
     * Pay a payment link (initiate payment)
     */
    async payPaymentLinkApi(payload: {
        shortCode: string;
        variantId?: string;
        quantity?: number;
        customAmount?: number;
        phoneNumber: string;
        operator: string;
        dynamicFields?: Record<string, unknown>;
    }) {
        const { shortCode, ...body } = payload;
        const { data } = await this.apiGatewayClient.post(
            `/api/v1/checkout/${encodeURIComponent(shortCode)}/pay`,
            body
        );
        return data;
    }

    /**
     * Resolve payment link price with variants/quantity/tiers
     */
    async resolvePaymentLinkPriceApi(payload: {
        shortCode: string;
        variantId?: string;
        quantity?: number;
        customAmount?: number;
    }) {
        const { shortCode, ...params } = payload;
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/${encodeURIComponent(shortCode)}/resolve-price`,
            { params }
        );
        return data;
    }

    /**
     * Get public storefront by slug (for checkout/public view)
     */
    async getPublicStorefrontApi(
        slug: string,
        locale?: string
    ): Promise<PublicStorefrontApiResponse> {
        const params = locale ? { locale } : undefined;
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/store/${encodeURIComponent(slug)}`,
            { params }
        );
        return data;
    }

    /**
     * Preview storefront by slug (includes unpublished/draft)
     */
    async getPublicStorefrontPreviewApi(
        slug: string,
        locale?: string
    ): Promise<PublicStorefrontApiResponse> {
        const params = locale ? { locale } : undefined;
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/store/${encodeURIComponent(slug)}/preview`,
            { params }
        );
        return data;
    }

    /**
     * Get public event by short code
     */
    async getPublicEventApi(shortCode: string) {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/events/${encodeURIComponent(shortCode)}`
        );
        return data;
    }

    /**
     * Save registration draft for session recovery (live mode only)
     */
    async saveRegistrationDraftApi(payload: {
        shortCode: string;
        sessionToken: string;
        ticketTypeId: string;
        phoneNumber: string;
        operator?: string;
        userFields?: { name: string; value: string }[];
        menuSelections?: Record<string, unknown>;
        customSectionData?: Record<string, Record<string, unknown>>;
        stepIndex?: number;
    }) {
        const { shortCode, ...body } = payload;
        const { data } = await this.apiGatewayClient.post(
            `/api/v1/checkout/events/${encodeURIComponent(shortCode)}/registration-draft`,
            body
        );
        return data;
    }

    /**
     * Get registration draft (for resume prompt)
     */
    async getRegistrationDraftApi(shortCode: string, sessionToken: string) {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/events/${encodeURIComponent(shortCode)}/registration-draft`,
            { params: { sessionToken } }
        );
        return data;
    }

    /**
     * Clear registration draft (call when payment completes)
     */
    async deleteRegistrationDraftApi(shortCode: string, sessionToken: string) {
        await this.apiGatewayClient.delete(
            `/api/v1/checkout/events/${encodeURIComponent(shortCode)}/registration-draft`,
            { params: { sessionToken } }
        );
    }

    /**
     * Register for an event (initiate payment)
     */
    async registerForEventApi(payload: {
        shortCode: string;
        ticketTypeId: string;
        phoneNumber: string;
        operator?: string;
        userFields?: { name: string; value: string }[];
        quantity?: number;
    }) {
        const { shortCode, ...body } = payload;
        const { data } = await this.apiGatewayClient.post(
            `/api/v1/checkout/events/${encodeURIComponent(shortCode)}/register`,
            body
        );
        return data;
    }

    /**
     * Get event registration / payment status
     */
    async getEventRegistrationStatusApi(shortCode: string, transactionRef: string) {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/${encodeURIComponent(shortCode)}/status/${encodeURIComponent(transactionRef)}`
        );
        return data;
    }

    /**
     * Get payment-link payment status. Includes redirectUrl on success and
     * failureRedirectUrl on failure (used to redirect the hosted checkout).
     */
    async getPaymentLinkPaymentStatusApi(
        shortCode: string,
        transactionRef: string
    ): Promise<{
        status: string;
        message?: string;
        redirectUrl?: string;
        failureRedirectUrl?: string;
    }> {
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/${encodeURIComponent(shortCode)}/status/${encodeURIComponent(transactionRef)}`
        );
        return data;
    }

    /**
     * Find which storefront contains a given event (by shortCode)
     */
    async getEventStorefrontApi(
        shortCode: string
    ): Promise<{ slug: string; storefrontPageId: string } | null> {
        try {
            const { data } = await this.apiGatewayClient.get(
                `/api/v1/checkout/events/${encodeURIComponent(shortCode)}/storefront`
            );
            return data;
        } catch {
            return null;
        }
    }

    /**
     * Find which storefront contains a given payment link (by shortCode)
     */
    async getPaymentLinkStorefrontApi(
        shortCode: string
    ): Promise<{ slug: string; storefrontPageId: string; itemId?: string } | null> {
        try {
            const { data } = await this.apiGatewayClient.get(
                `/api/v1/checkout/payment-links/${encodeURIComponent(shortCode)}/storefront`
            );
            return data;
        } catch {
            return null;
        }
    }

    /**
     * Get enriched storefront item (product detail page) by storefront slug + item ID.
     * When preview is true, includes unpublished storefronts (for storefront preview mode).
     */
    async getPublicStorefrontItemApi(
        slug: string,
        itemId: string,
        locale?: string,
        options?: { preview?: boolean }
    ) {
        const params: Record<string, string> = locale ? { locale } : {};
        if (options?.preview) {
            params.preview = "1";
        }
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/checkout/store/${encodeURIComponent(slug)}/item/${encodeURIComponent(itemId)}`,
            { params: Object.keys(params).length > 0 ? params : undefined }
        );
        return data;
    }

    /**
     * Cart checkout (public): creates a 24h invoice from the cart and either
     * initiates a MoMo/OM collection now or emails the invoice to pay later.
     */
    async createStorefrontCheckoutApi(
        slug: string,
        payload: {
            lineItems: Array<{
                itemId: string;
                quantity: number;
                variantId?: string;
                note?: string;
            }>;
            mode: "pay_now" | "email";
            customerName: string;
            customerPhone: string;
            customerEmail?: string;
            operator?: "MTN" | "ORANGE";
            /** Pay this % now (1-99) instead of the full total; only applies to mode "pay_now". */
            depositPercent?: number;
            /** Pay in N (2-4) interest-free installments instead; only applies to mode "pay_now". Mutually exclusive with depositPercent. */
            installments?: number;
        }
    ): Promise<
        | { invoiceId: string; mode: "pay_now"; transactionRef: string }
        | { invoiceId: string; mode: "email"; payUrl: string }
    > {
        const { data } = await this.apiGatewayClient.post(
            `/api/v1/checkout/store/${encodeURIComponent(slug)}/create-invoice`,
            payload
        );
        return data;
    }

    /**
     * Preview an unpublished storefront (admin preview)
     */
    async getStorefrontPreviewApi(
        storefrontId: string,
        locale?: string
    ): Promise<PublicStorefrontApiResponse> {
        const params = locale ? { locale } : undefined;
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/storefront/pages/${encodeURIComponent(storefrontId)}/preview`,
            { params }
        );
        return data;
    }

    /**
     * Preview a template with its default sample content
     */
    async getTemplatePreviewApi(
        templateSlug: string,
        locale?: string
    ): Promise<PublicStorefrontApiResponse> {
        const params = locale ? { locale } : undefined;
        const { data } = await this.apiGatewayClient.get(
            `/api/v1/storefront/templates/${encodeURIComponent(templateSlug)}/preview`,
            { params }
        );
        return data;
    }

    // Downloads the ticket as a PNG image for a completed registration
    async downloadPartyPlugTicketApi(id: string): Promise<void> {
        const { data, headers } = await this.apiGatewayClient.get(
            `/api/v1/events/party-plug/ticket/${id}/download`,
            {
                responseType: "blob",
                headers: {
                    Accept: "image/png",
                },
            }
        );

        // Extract filename from Content-Disposition header or use default
        const contentDisposition = headers["content-disposition"];
        let filename = `ticket-${id}.png`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Create blob and trigger download
        const blob = new Blob([data], { type: "image/png" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}
